import React, { FC, ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { Link, Paper, TablePagination, Typography } from '@material-ui/core';
import MaterialTable from 'material-table';
import { useIntl } from 'react-intl';

import type { RootState } from '../../store/store';
import { name as channelFacetSelector, setCount, setPage } from '../../store/reducers/channelFacet';
import { useDebouncedEffect } from '../../hooks';
import ChannelsFilters from './ChannelsFilters';
import { useLanguageContext } from '../languages';
import { listChannels } from './apiQueries';
import { Channel, StringFilterInput } from '../../utils/types';
import commonMessages from '../common/messages';

const PAGE_SIZE = 15;
const SORT_DIRECTION = 'DESC';

const Channels: FC = () => {
	const dispatch = useDispatch();

	// All these search facets come from the channelFacetSelector in the redux store
	const { id, name, territory, type, highlights, page, count } = useSelector((storedState: RootState) => storedState[channelFacetSelector]);

	// debouncedId and debouncedName apply a 500ms pause on keyboard input of id and name fields
	// initialised with channelFacetSelector props to avoid debouncing page reload
	const [debouncedId, setDebouncedId] = useDebouncedEffect(id || '', 500);
	const [debouncedName, setDebouncedName] = useDebouncedEffect(name || '', 500);

	// on update set the debounced values
	useEffect(() => {
		setDebouncedId(id);
		setDebouncedName(name);
	}, [id, name, setDebouncedId, setDebouncedName]);

	const initialPageSize = PAGE_SIZE * 4;

	const intl = useIntl();
	const { tableLocalization } = useLanguageContext();

	// Set up query filters
	const filters: ChannelFiltersInput = {
		type: type ? { eq: type } : { ne: 'ARCHIVED' }
	};

	// The main query hook
	const { loading, error, data, fetchMore, refetch } = useQuery(listChannels, {
		variables: {
			limit: initialPageSize,
			nextToken: null,
			sortDirection: SORT_DIRECTION,
			filter: Object.keys(filters).length > 0 ? filters : null,
			territory: territory ? territory : null
		}
	});

	// Requery when filters update
	useEffect(() => {
		refetch({
			limit: initialPageSize,
			nextToken: null,
			sortDirection: SORT_DIRECTION,
			filter: Object.keys(filters).length > 0 ? filters : null,
			territory: territory ? territory : null
		});
	}, [filters, territory, refetch, dispatch, page, count, initialPageSize]);

	if (loading) return <Typography>Loading...</Typography>;
	if (error) return <Typography>Error loading data from the server</Typography>;
	if (!data.listChannels) return <Typography>No channels found</Typography>;

	const handleLoadMore = (): void => {
		const { nextToken } = data.listChannels;
		if (nextToken) {
			fetchMore({
				query: listChannels,
				variables: { nextToken, limit: PAGE_SIZE, SORT_DIRECTION, filter: Object.keys(filters).length > 0 ? filters : null },
				updateQuery: (previousResult: listChannelsData, { fetchMoreResult }): listChannelsData => {
					if (!fetchMoreResult) return previousResult;

					const previousItems = previousResult.listChannels.items;
					const newItems = fetchMoreResult.listChannels.items;
					const newNextToken = fetchMoreResult.listChannels.nextToken;

					dispatch(setCount(previousItems.length + newItems.length));

					return {
						listChannels: {
							items: [...previousItems, ...newItems],
							nextToken: newNextToken,
							__typename: 'ChannelConnection'
						}
					};
				}
			});
		}
	};

	const handleChangePage = (page: number): void => {
		handleLoadMore();
		dispatch(setPage(page));
	};

	const channels = data?.listChannels.items;
	if (!channels || channels.length === 0) return <Typography>No channels found</Typography>;

	const filteredChannels = channels.filter((channel: Channel) => {
		if (debouncedId && !channel.id.includes(debouncedId)) return false;
		if (debouncedName && channel.name && !channel.name.includes(debouncedName)) return false;
		if (highlights && !channel.highlights) return false;
		return true;
	});

	filteredChannels.sort((a: Channel, b: Channel) => (a.name!.toLowerCase() > b.name!.toLowerCase() ? 1 : -1));

	const currentPage = filteredChannels.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

	return (
		<>
			<ChannelsFilters />
			<MaterialTable
				localization={tableLocalization}
				columns={[
					{
						title: 'Id',
						field: 'id',
						render: (row: Channel): JSX.Element => (
							<Link component={RouterLink} to={`/channels/${encodeURIComponent(row.id)}`} color='secondary'>
								{row.id}
							</Link>
						)
					},
					{ title: 'Name', field: 'name' },
					{
						title: 'Language',
						field: 'language',
						lookup: {
							en: 'en',
							de: 'de',
							it: 'it',
							fr: 'fr',
							es: 'es'
						}
					},
					{
						title: 'Territory',
						field: 'territory',
						lookup: {
							GB: 'GB',
							DE: 'DE',
							IT: 'IT',
							FR: 'FR',
							AT: 'AT',
							US: 'US',
							ES: 'ES'
						}
					},
					{
						title: 'Type',
						field: 'type',
						lookup: {
							LIVE: 'LIVE',
							TEST: 'TEST',
							EXTERNAL: 'EXTERNAL'
						}
					},
					{
						title: 'Highlights',
						field: 'highlights',
						type: 'boolean'
					}
				]}
				data={currentPage}
				options={{ pageSize: PAGE_SIZE, search: false, sorting: false }}
				title='Channels'
				components={{
					Container: (props): JSX.Element => <Paper {...props} data-testid='channels-table' />,
					Pagination: ({ style }): JSX.Element => (
						<TablePagination
							style={style}
							rowsPerPageOptions={[PAGE_SIZE]}
							rowsPerPage={PAGE_SIZE}
							count={count || data.listChannels.items.length}
							page={page}
							onPageChange={(e, page): void => handleChangePage(page)}
							labelDisplayedRows={({ from, to }): ReactNode =>
								intl.formatMessage(
									{
										...commonMessages.paginationLabel
									},
									{ from, to }
								)
							}
						/>
					)
				}}
			/>
		</>
	);
};

type listChannelsData = {
	listChannels: {
		items: Channel[];
		nextToken: string;
		__typename: 'ChannelConnection';
	};
};

type ChannelFiltersInput = {
	type?: StringFilterInput | null;
};

type PostQueryFiltersInput = {
	id?: string;
	name?: string;
};

export default Channels;
